import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
// @ts-nocheck — TODO: add explicit types to lambda params
import Table from "@amzn/awsui-components-react/polaris/table";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Box from "@amzn/awsui-components-react/polaris/box";
import TextFilter from "@amzn/awsui-components-react/polaris/text-filter";
import Pagination from "@amzn/awsui-components-react/polaris/pagination";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Select from "@amzn/awsui-components-react/polaris/select";
import Multiselect from "@amzn/awsui-components-react/polaris/multiselect";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import Checkbox from "@amzn/awsui-components-react/polaris/checkbox";
import Grid from "@amzn/awsui-components-react/polaris/grid";
import Link from "@amzn/awsui-components-react/polaris/link";
import StatusIndicator from "@amzn/awsui-components-react/polaris/status-indicator";
import { useCollection } from "@amzn/awsui-collection-hooks";
import { evalApi } from "../../client/EvalApiClient";
import SafeTrans from "../../../i18n/SafeTrans";
import {
  DEFAULT_AGENT,
  SKILL_TAG_AGENTS,
  domainTagToText,
  effectiveAgent,
  filterByAgent,
  skillAgentFor,
  keepDomainTagAgents,
  parseDomainTag,
  type DomainTag,
  type SkillTag,
} from "./goldenAgents";
import "./GoldenDatasetsV2Page.css";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GoldenTestCase {
  suite: string;
  test_case_id: string;
  input_prompt?: string;
  turns?: string[];
  expected_output?: string;
  context?: Record<string, unknown>;
  expected_tools?: string[];
  metrics?: string[];
  skills_tag?: SkillTag[];
  domains_tag?: (string | DomainTag)[];
  /** Agent this case runs on; absent/null = default (the run's agent, rocky). */
  agent_name?: string | null;
  personal_profile?: string;
  /** Legacy read compatibility only. New writes use personal_profile. */
  user_alias?: string;
  channel_type?: string;
  online_ignore?: boolean;
  is_scheduled?: boolean;
  /** Marks the case eligible for the integration-test gate (Hydra approval
   *  workflow). Orthogonal to online_ignore/is_scheduled. */
  integration_test?: boolean;
  status: string;
  version: number;
  created_by?: string;
  source_candidate_id?: string;
}

interface ListResponse {
  items: GoldenTestCase[];
  count: number;
  next_token: string | null;
}

interface EvaluationProfile {
  profile_id: string;
  display_alias: string;
  enabled: boolean;
}

interface EvaluationProfilesResponse {
  profiles: EvaluationProfile[];
  count: number;
}

// ---------------------------------------------------------------------------
// API Helpers
// ---------------------------------------------------------------------------

const GOLDEN_DATASET_API_PAGE_SIZE = 200;

export async function listAllCases(): Promise<GoldenTestCase[]> {
  const cases: GoldenTestCase[] = [];
  let nextToken: string | null = null;

  do {
    const params = new URLSearchParams({ limit: String(GOLDEN_DATASET_API_PAGE_SIZE) });
    if (nextToken) params.set("next_token", nextToken);

    const res: ListResponse = await evalApi.signedRequest(
      "GET",
      `/golden-dataset?${params.toString()}`,
    );
    cases.push(...(res.items || []));
    nextToken = res.next_token || null;
  } while (nextToken);

  return cases;
}

async function listSuites(): Promise<string[]> {
  const res = await evalApi.signedRequest("GET", "/golden-dataset/suites");
  return res.suites || [];
}

async function listEvaluationProfiles(): Promise<EvaluationProfile[]> {
  const res: EvaluationProfilesResponse = await evalApi.signedRequest(
    "GET",
    "/evaluation-profiles",
  );
  return res.profiles || [];
}

async function createCase(body: Partial<GoldenTestCase>): Promise<GoldenTestCase> {
  return evalApi.signedRequest("POST", "/golden-dataset", JSON.stringify(body));
}

async function updateCase(suite: string, id: string, body: Record<string, unknown>): Promise<GoldenTestCase> {
  return evalApi.signedRequest("PUT", `/golden-dataset/${encodeURIComponent(suite)}/${encodeURIComponent(id)}`, JSON.stringify(body));
}

async function deleteCase(suite: string, id: string): Promise<unknown> {
  return evalApi.signedRequest("DELETE", `/golden-dataset/${encodeURIComponent(suite)}/${encodeURIComponent(id)}`);
}

async function getUploadUrl(filename: string): Promise<{ upload_url: string; s3_key: string }> {
  return evalApi.signedRequest("POST", "/golden-dataset/upload-url", JSON.stringify({ filename }));
}

async function uploadFileToS3(url: string, file: File): Promise<void> {
  await fetch(url, { method: "PUT", body: file, headers: { "Content-Type": "application/json" } });
}

async function triggerImport(s3Key: string): Promise<{ imported: number; errors: number; error_details: Array<{ error: string; test_case_id: string }> }> {
  return evalApi.signedRequest("POST", "/golden-dataset/import", JSON.stringify({ s3_key: s3Key }));
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const METRICS_OPTIONS = [
  "correctness",
  "relevance",
  "goal_achievement",
  "tool_selection",
  "tool_params_accuracy",
];

const STATUS_OPTIONS = ["active", "inactive", "draft"] as const;

const CHANNEL_TYPE_OPTIONS = ["rocky", "slack", "teams", "api"];



const PAGE_SIZE = 10;

// ---------------------------------------------------------------------------
// Form state defaults
// ---------------------------------------------------------------------------

interface FormState {
  test_case_id: string;
  suite: string;
  metrics: string;
  status: string;
  channel_type: string;
  personal_profile: string;
  expected_tools: string;
  /** Owning agent for this case's skill tags — the (agent, skill) join key the
   *  eval backend selects on. All skill_tag names below share this agent. */
  skill_agent: string;
  /** Skill tag NAMES only (comma-separated); the agent is skill_agent above. */
  skills_tag: string;
  domains_tag: string;
  /** Agent this case runs on ("" = default, rocky). */
  run_agent: string;
  online_ignore: boolean;
  is_scheduled: boolean;
  integration_test: boolean;
  input_mode: "single" | "multi";
  input_prompt: string;
  turns: string[];
  expected_output: string;
  context_raw: string;
  context_mode: "raw" | "structured";
  /** The edited row's current version, carried through for the update's
   *  optimistic-locking check (the backend requires it on PUT). 0 on create —
   *  create doesn't send/require a version (a new row starts at 1). */
  version: number;
}

const EMPTY_FORM: FormState = {
  test_case_id: "",
  suite: "",
  metrics: METRICS_OPTIONS.join(", "),
  status: "active",
  channel_type: "rocky",
  personal_profile: "",
  expected_tools: "",
  skill_agent: SKILL_TAG_AGENTS[0],
  skills_tag: "",
  domains_tag: "",
  run_agent: "",
  online_ignore: false,
  is_scheduled: false,
  integration_test: false,
  input_mode: "single",
  input_prompt: "",
  turns: [""],
  expected_output: "",
  context_raw: "{}",
  context_mode: "raw",
  version: 0,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function ContextStructuredEditor({ contextRaw, onChange }: { contextRaw: string; onChange: (v: string) => void }) {
  const { t } = useTranslation("eval");
  const [parsed, setParsed] = useState<Record<string, string>>({});
  // A parse problem is kept as its key and translated at render.
  const [parseError, setParseError] = useState<"" | "mustBeObject" | "invalidJson">("");
  const [newKey, setNewKey] = useState("");
  const [showNewKey, setShowNewKey] = useState(false);

  useEffect(() => {
    if (!contextRaw.trim()) { setParsed({}); setParseError(""); return; }
    try {
      const obj = JSON.parse(contextRaw);
      if (typeof obj === "object" && obj !== null && !Array.isArray(obj)) {
        const flat: Record<string, string> = {};
        for (const [k, v] of Object.entries(obj)) { flat[k] = typeof v === "string" ? v : JSON.stringify(v); }
        setParsed(flat); setParseError("");
      } else { setParseError("mustBeObject"); }
    } catch { setParseError("invalidJson"); }
  }, [contextRaw]);

  const syncToJson = (obj: Record<string, string>) => {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) { try { result[k] = JSON.parse(v); } catch { result[k] = v; } }
    onChange(JSON.stringify(result, null, 2));
  };

  const updateField = (key: string, val: string) => { const next = { ...parsed, [key]: val }; setParsed(next); syncToJson(next); };
  const removeField = (key: string) => { const next = { ...parsed }; delete next[key]; setParsed(next); syncToJson(next); };
  const addField = () => { const k = newKey.trim(); if (!k || k in parsed) return; const next = { ...parsed, [k]: "" }; setParsed(next); syncToJson(next); setNewKey(""); setShowNewKey(false); };

  const sortedKeys = Object.keys(parsed).sort((a, b) => (parsed[a] || "").length - (parsed[b] || "").length);

  return (
    <div>
      {parseError && <div style={{ color: "#db0000", fontSize: 13, marginBottom: 8 }}>{t(`datasets.ctx.${parseError}`)}</div>}
      {sortedKeys.length === 0 && !parseError && (
        <div style={{ color: "#8c8c94", fontSize: 13, padding: "12px 0" }}>{t("datasets.ctx.noFields")}</div>
      )}
      {sortedKeys.map((key) => (
        <div key={key} style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#0f141a" }}>{key}</span>
            <button onClick={() => removeField(key)} style={{ background: "none", border: "none", color: "#db0000", cursor: "pointer", fontSize: 14 }}>&times;</button>
          </div>
          <textarea
            value={parsed[key]}
            onChange={(e) => updateField(key, e.target.value)}
            rows={Math.max(2, Math.min(8, Math.ceil((parsed[key] || "").length / 60)))}
            placeholder={t("datasets.ctx.enterValue", { field: key })}
            style={{ width: "100%", padding: "8px 12px", border: "1px solid #e9ebed", borderRadius: 8, fontSize: 13, fontFamily: key.includes("sql") ? "monospace" : "inherit", resize: "vertical" }}
          />
        </div>
      ))}
      <div style={{ marginTop: 8 }}>
        {showNewKey ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addField(); if (e.key === "Escape") setShowNewKey(false); }}
              placeholder="field_name"
              autoFocus
              style={{ padding: "6px 12px", border: "1px solid #e9ebed", borderRadius: 6, fontSize: 13, width: 200 }}
            />
            <button onClick={addField} style={{ padding: "6px 14px", background: "#0073bb", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{t("datasets.ctx.add")}</button>
            <button onClick={() => setShowNewKey(false)} style={{ padding: "6px 14px", background: "none", color: "#414d5c", border: "1px solid #e9ebed", borderRadius: 6, fontSize: 13, cursor: "pointer" }}>{t("datasets.ctx.cancel")}</button>
          </div>
        ) : (
          <button onClick={() => setShowNewKey(true)} style={{ padding: "6px 14px", background: "none", color: "#0073bb", border: "1px solid #0073bb", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{t("datasets.ctx.addKey")}</button>
        )}
      </div>
    </div>
  );
}

export default function GoldenDatasetsV2Page() {
  const { t } = useTranslation("eval");
  // Data state
  const navigate = useNavigate();
  const [candidateCount, setCandidateCount] = useState(0);
  const [allCases, setAllCases] = useState<GoldenTestCase[]>([]);
  const [suites, setSuites] = useState<string[]>([]);
  const [evaluationProfiles, setEvaluationProfiles] = useState<EvaluationProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState<GoldenTestCase[]>([]);

  // Filter state
  const [suiteFilter, setSuiteFilter] = useState<string>("__all__");
  // The eval pages' selected agent (EvalLayout outlet): the list shows its cases only.
  const currentAgent = useOutletContext<{ agent?: string } | undefined>()?.agent || DEFAULT_AGENT;
  const [statusFilter, setStatusFilter] = useState<string>("active");

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCase, setEditingCase] = useState<GoldenTestCase | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error" | "info"; msg: string } | null>(null);
  const [importResult, setImportResult] = useState<{ imported: number; errors: number; details: Array<{ error: string; test_case_id: string }> } | null>(null);

  // ------ Data loading ------
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [cases, suiteList, profiles] = await Promise.all([
        listAllCases(),
        listSuites(),
        listEvaluationProfiles(),
      ]);
      setAllCases(cases);
      setSuites(suiteList);
      setEvaluationProfiles(profiles);
    } catch (err) {
      console.error("Failed to load golden datasets", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    evalApi.signedRequest("GET", "/golden-candidates?status=pending")
      .then((d: any) => setCandidateCount(d.count || 0))
      .catch(() => {});
  }, []);

  // ------ Derived filtered items ------
  const filteredItems = filterByAgent(allCases, currentAgent).filter((item) => {
    if (suiteFilter !== "__all__" && item.suite !== suiteFilter) return false;
    if (statusFilter !== "__all__") { if (statusFilter === "inactive" ? !(item.status === "inactive" || item.status === "archived") : item.status !== statusFilter) return false; }
    return true;
  });

  // ------ useCollection for text filter + pagination ------
  const { items, filterProps, paginationProps, collectionProps } = useCollection(filteredItems, {
    filtering: {
      empty: <Box textAlign="center" color="inherit"><b>{t("datasets.noCases")}</b></Box>,
      noMatch: <Box textAlign="center" color="inherit"><b>{t("datasets.noMatches")}</b></Box>,
      filteringFunction: (item, filterText) => {
        const lower = filterText.toLowerCase();
        return (
          (item.test_case_id || "").toLowerCase().includes(lower) ||
          (item.input_prompt || "").toLowerCase().includes(lower) ||
          (item.suite || "").toLowerCase().includes(lower)
        );
      },
    },
    pagination: { pageSize: PAGE_SIZE },
    sorting: {},
    selection: { trackBy: (item) => `${item.suite}#${item.test_case_id}` },
  });

  // ------ Actions ------
  // Toasts are short-lived, so their text is built when they are shown.
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();
  const errorText = (err: unknown) => (err instanceof Error ? err.message : t("datasets.unknownError"));
  const showToast = (type: "success" | "error" | "info", msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ type, msg });
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  };

  const handleDelete = async () => {
    if (selectedItems.length === 0) return;
    if (!window.confirm(t("datasets.confirmDelete", { n: selectedItems.length }))) return;
    try {
      await Promise.all(selectedItems.map((c) => deleteCase(c.suite, c.test_case_id)));
      showToast("success", t("datasets.deleted", { n: selectedItems.length }));
      setSelectedItems([]);
      await loadData();
    } catch (err) {
      showToast("error", t("datasets.deleteFailed", { error: errorText(err) }));
    }
  };

  const handleImportJson = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.multiple = true;
    input.onchange = async (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files || files.length === 0) return;
      let totalImported = 0;
      let totalErrors = 0;
      let allDetails: Array<{ error: string; test_case_id: string }> = [];
      try {
        for (const file of Array.from(files)) {
          const { upload_url, s3_key } = await getUploadUrl(file.name);
          await uploadFileToS3(upload_url, file);
          const result = await triggerImport(s3_key);
          totalImported += result.imported;
          totalErrors += result.errors;
          if (result.error_details) allDetails = allDetails.concat(result.error_details);
        }
        if (totalErrors > 0) {
          setImportResult({ imported: totalImported, errors: totalErrors, details: allDetails });
        } else {
          showToast("success", t("datasets.imported", { n: totalImported }));
        }
      } catch (err) {
        showToast("error", t("datasets.importFailed", { error: errorText(err) }));
      }
    };
    input.click();
  };

  const openCreateModal = () => {
    setEditingCase(null);
    // Prefill from the current agent: its skill tags, and run on it unless it is the default.
    setForm({
      ...EMPTY_FORM,
      run_agent: currentAgent !== DEFAULT_AGENT ? currentAgent : "",
      skill_agent: skillAgentFor(null, currentAgent),
    });
    setModalVisible(true);
  };

  const profileIdForCase = (c: GoldenTestCase): string => {
    if (c.personal_profile) return c.personal_profile;
    const legacyAlias = c.user_alias?.trim().toLowerCase();
    if (!legacyAlias) return "";
    return evaluationProfiles.find(
      (profile) => profile.display_alias.toLowerCase() === legacyAlias,
    )?.profile_id || "";
  };

  const openEditForItem = (c: GoldenTestCase) => {
    setEditingCase(c);
    const isMulti = !!(c.turns && c.turns.length > 1);
    setForm({
      test_case_id: c.test_case_id,
      suite: c.suite,
      metrics: (c.metrics || []).join(", "),
      status: c.status,
      channel_type: c.channel_type || "rocky",
      personal_profile: profileIdForCase(c),
      expected_tools: (c.expected_tools || []).join(", "),
      skills_tag: (c.skills_tag || []).map((s) => s.skill).join(", "),
      domains_tag: (c.domains_tag || []).map(domainTagToText).join(", "),
      run_agent: c.agent_name || "",
      online_ignore: c.online_ignore || false,
      is_scheduled: c.is_scheduled || false,
      input_mode: isMulti ? "multi" : "single",
      input_prompt: isMulti ? "" : (c.turns?.[0] || c.input_prompt || ""),
      turns: isMulti ? c.turns! : [""],
      expected_output: c.expected_output || "",
      context_raw: c.context ? JSON.stringify(c.context, null, 2) : "{}",
      skill_agent: skillAgentFor(c, currentAgent),
      integration_test: c.integration_test || false,
      context_mode: "raw",
      version: c.version,
    });
    setModalVisible(true);
  };

  const openEditModal = () => {
    if (selectedItems.length !== 1) return;
    const c = selectedItems[0];
    setEditingCase(c);
    const isMulti = !!(c.turns && c.turns.length > 1);
    setForm({
      test_case_id: c.test_case_id,
      suite: c.suite,
      metrics: (c.metrics || []).join(", "),
      status: c.status,
      channel_type: c.channel_type || "rocky",
      personal_profile: profileIdForCase(c),
      expected_tools: (c.expected_tools || []).join(", "),
      skill_agent: skillAgentFor(c, currentAgent),
      skills_tag: (c.skills_tag || []).map((s) => s.skill).join(", "),
      domains_tag: (c.domains_tag || []).map(domainTagToText).join(", "),
      run_agent: c.agent_name || "",
      online_ignore: c.online_ignore || false,
      is_scheduled: c.is_scheduled || false,
      integration_test: c.integration_test || false,
      input_mode: isMulti ? "multi" : "single",
      input_prompt: isMulti ? "" : (c.turns?.[0] || c.input_prompt || ""),
      turns: isMulti ? c.turns! : [""],
      expected_output: c.expected_output || "",
      context_raw: c.context ? JSON.stringify(c.context, null, 2) : "{}",
      context_mode: "raw",
      version: c.version,
    });
    setModalVisible(true);
  };

  const doSubmit = async (overrides?: Partial<FormState>) => {
    const effective = overrides ? { ...form, ...overrides } : form;
    setSubmitting(true);
    try {
      const skillsTags: SkillTag[] = effective.skills_tag
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((skill) => ({ agent: effective.skill_agent, skill }));

      let parsedContext: Record<string, unknown> = {};
      try {
        parsedContext = JSON.parse(effective.context_raw);
      } catch {
        parsedContext = {};
      }

      const effectiveSuite = effective.suite === "__new__" ? ((effective as any)._newSuite || "").trim() : effective.suite.trim();
      if (!effective.personal_profile) {
        throw new Error(t("datasets.profileRequired"));
      }
      const body: Partial<GoldenTestCase> = {
        test_case_id: effective.test_case_id.trim(),
        suite: effectiveSuite,
        metrics: effective.metrics.split(",").map((s) => s.trim()).filter(Boolean),
        status: effective.status,
        channel_type: effective.channel_type,
        personal_profile: effective.personal_profile,
        expected_tools: effective.expected_tools.split(",").map((s) => s.trim()).filter(Boolean),
        skills_tag: skillsTags.length > 0 ? skillsTags : undefined,
        // Each entry is "domain" (whole domain) or "domain/sub_domain" (one
        // slice). parseDomainTag emits a bare string or {domain, sub_domain};
        // the write API accepts both and normalizes on store.
        domains_tag: keepDomainTagAgents(
          effective.domains_tag
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
            .map(parseDomainTag),
          editingCase?.domains_tag,
        ),
        agent_name: effective.run_agent || null,
        online_ignore: effective.online_ignore,
        is_scheduled: effective.is_scheduled,
        integration_test: effective.integration_test,
        expected_output: effective.expected_output.trim() || undefined,
        context: Object.keys(parsedContext).length > 0 ? parsedContext : undefined,
      };

      if (effective.input_mode === "single") {
        (body as Record<string, unknown>).input_prompt = effective.input_prompt.trim();
        (body as Record<string, unknown>).turns = undefined;
      } else {
        body.turns = effective.turns.filter((turn) => turn.trim().length > 0);
        (body as Record<string, unknown>).input_prompt = "";
      }

      if (editingCase) {
        (body as Record<string, unknown>).version = editingCase.version;
        await updateCase(editingCase.suite, editingCase.test_case_id, body as Record<string, unknown>);
      } else {
        await createCase(body);
      }

      setModalVisible(false);
      showToast("success", editingCase ? t("datasets.updated") : t("datasets.created"));
      setSelectedItems([]);
      await loadData();
    } catch (err) {
      showToast("error", t("datasets.opFailed", { error: errorText(err) }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (form.input_mode === "multi") {
      const effectiveTurns = form.turns.filter((turn) => turn.trim().length > 0);
      if (effectiveTurns.length <= 1) {
        const normalized: Partial<FormState> = {
          input_mode: "single" as const,
          input_prompt: effectiveTurns[0] || "",
        };
        showToast("info", t("datasets.oneTurn"));
        setForm((f) => ({ ...f, ...normalized }));
        await doSubmit(normalized);
        return;
      }
    }
    await doSubmit();
  };

  // ------ Turn management ------
  const addTurn = () => setForm((f) => ({ ...f, turns: [...f.turns, ""] }));
  const updateTurn = (index: number, value: string) =>
    setForm((f) => {
      const turns = [...f.turns];
      turns[index] = value;
      return { ...f, turns };
    });
  const removeTurn = (index: number) =>
    setForm((f) => {
      const turns = f.turns.filter((_, i) => i !== index);
      return { ...f, turns: turns.length > 0 ? turns : [""] };
    });

  // ------ Column Definitions ------
  const columnDefinitions = [
    {
      id: "test_case_id",
      header: t("datasets.col.id"),
      cell: (item: GoldenTestCase) => item.test_case_id,
      sortingField: "test_case_id",
    },
    {
      id: "mode",
      header: t("datasets.col.mode"),
      cell: (item: GoldenTestCase) => {
        if (item.turns && item.turns.length > 1) return t("datasets.multiTurn");
        return t("datasets.singleTurn");
      },
    },
    {
      id: "input_prompt",
      header: t("datasets.col.prompt"),
      cell: (item: GoldenTestCase) => {
        const prompt = item.turns && item.turns.length > 0 ? item.turns[0] : item.input_prompt || "";
        if (!prompt) return "-";
        const display = prompt.length > 60 ? prompt.substring(0, 60) + "..." : prompt;
        return (
          <Link variant="primary" onFollow={(e) => { e.preventDefault(); openEditForItem(item); }}>
            {display}
          </Link>
        );
      },
    },
    {
      id: "agent",
      header: t("datasets.col.agent"),
      cell: (item: GoldenTestCase) => {
        const { agent, isDefault } = effectiveAgent(item);
        return isDefault ? t("datasets.agentDefault") : agent;
      },
    },
    {
      id: "suite",
      header: t("datasets.col.suite"),
      cell: (item: GoldenTestCase) => item.suite,
      sortingField: "suite",
    },
    {
      id: "personal_profile",
      header: t("datasets.col.profile"),
      cell: (item: GoldenTestCase) => {
        const profileId = profileIdForCase(item);
        const profile = evaluationProfiles.find((entry) => entry.profile_id === profileId);
        if (profile) {
          const shortId = `${profile.profile_id.slice(0, 10)}…${profile.profile_id.slice(-6)}`;
          return `${profile.display_alias} · ${shortId}`;
        }
        if (item.personal_profile) return item.personal_profile;
        if (item.user_alias) return t("datasets.legacyAlias", { alias: item.user_alias });
        return "-";
      },
    },
    {
      id: "source",
      header: t("datasets.col.source"),
      cell: (item: GoldenTestCase) => {
        if (item.source_candidate_id) return t("datasets.source.candidate");
        if (item.test_case_id?.startsWith("eval-candidate")) return t("datasets.source.candidate");
        if (item.created_by === "migration") return t("datasets.source.migration");
        if (item.created_by === "import") return t("datasets.source.import");
        return t("datasets.source.manual");
      },
    },
    {
      id: "created_by",
      header: t("datasets.col.creator"),
      cell: (item: GoldenTestCase) => item.created_by || "-",
    },
    {
      id: "status",
      header: t("datasets.col.status"),
      cell: (item: GoldenTestCase) => {
        const type = item.status === "active" ? "success" : (item.status === "inactive" || item.status === "archived") ? "stopped" : "pending";
        return <StatusIndicator type={type}>{item.status}</StatusIndicator>;
      },
    },
  ];

  // ------ Suite filter options ------
  // Known statuses have a label; any other (e.g. legacy "archived") is title-cased.
  const statusLabel = (s: string) =>
    (STATUS_OPTIONS as readonly string[]).includes(s)
      ? t(`datasets.status.${s as (typeof STATUS_OPTIONS)[number]}`)
      : s.charAt(0).toUpperCase() + s.slice(1);

  const suiteOptions = [
    { label: t("datasets.allSuites"), value: "__all__" },
    ...suites.map((s) => ({ label: s, value: s })),
  ];

  const statusOptions = [
    { label: t("datasets.allStatuses"), value: "__all__" },
    ...STATUS_OPTIONS.map((s) => ({ label: statusLabel(s), value: s })),
  ];

  // ------ Render ------
  return (
    <div className="eval-gd">
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: "#0f141a", margin: "0 0 4px" }}>{t("datasets.title")}</h1>
        <p style={{ fontSize: 14, color: "#414d5c", margin: 0 }}>{t("datasets.subtitle")}</p>
      </div>
      <div className="eval-gd__card">
        <Header
          variant="h2"
          counter={`(${filteredItems.length})`}
          info={<Link variant="info">{t("datasets.info")}</Link>}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button disabled={selectedItems.length === 0} onClick={handleDelete}>
                {t("datasets.delete")}
              </Button>
              <Button disabled={selectedItems.length !== 1} onClick={openEditModal}>
                {t("datasets.edit")}
              </Button>
              <Button variant="normal" iconName="status-warning" onClick={() => navigate("/eval/candidates")}>{t("datasets.reviewCandidates", { n: candidateCount })}</Button>
              <Button variant="normal" iconName="upload" onClick={handleImportJson}>
                {t("datasets.importJson")}
              </Button>
              <Button variant="primary" onClick={openCreateModal}>
                {t("datasets.addCase")}
              </Button>
            </SpaceBetween>
          }
        >
          {t("datasets.header")}
        </Header>
        <SpaceBetween size="m">
          <div className="eval-gd__filters-row">
            <Select
              selectedOption={suiteOptions.find((o) => o.value === suiteFilter) || suiteOptions[0]}
              onChange={({ detail }) => setSuiteFilter(detail.selectedOption.value || "__all__")}
              options={suiteOptions}
              placeholder={t("datasets.allSuites")}
            />
            <Select
              selectedOption={statusOptions.find((o) => o.value === statusFilter) || statusOptions[0]}
              onChange={({ detail }) => setStatusFilter(detail.selectedOption.value || "__all__")}
              options={statusOptions}
              placeholder={t("datasets.statusPlaceholder")}
            />
            <div style={{ flex: 1 }}>
              <TextFilter {...filterProps} filteringPlaceholder={t("shared.findResources")} />
            </div>
            <Pagination {...paginationProps} />
          </div>
          <Table
            {...collectionProps}
            columnDefinitions={columnDefinitions}
            items={items}
            loading={loading}
            loadingText={t("datasets.loading")}
            selectionType="multi"
            selectedItems={selectedItems}
            onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
            trackBy={(item) => `${item.suite}#${item.test_case_id}`}
            empty={
              <Box textAlign="center" color="inherit">
                <b>{t("datasets.noCases")}</b>
                <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                  {t("datasets.noCasesBody")}
                </Box>
              </Box>
            }
          />

          <Box textAlign="center">
            <Link href="#" onFollow={(e) => { e.preventDefault(); setSuiteFilter("__all__"); setStatusFilter("__all__"); }}>
              {t("datasets.viewAll")}
            </Link>
          </Box>
        </SpaceBetween>
      </div>

      {/* Create / Edit Modal */}
      <Modal
        visible={modalVisible}
        onDismiss={() => setModalVisible(false)}
        header={editingCase ? t("datasets.modal.editTitle") : t("datasets.modal.createTitle")}
        size="max"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setModalVisible(false)}>
                {t("datasets.modal.cancel")}
              </Button>
              <Button variant="primary" loading={submitting} onClick={handleSubmit}>
                {t("datasets.modal.confirm")}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          {/* Row 1 */}
          <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
            <FormField label={t("datasets.col.id")}>
              <Input
                value={form.test_case_id}
                onChange={({ detail }) => setForm((f) => ({ ...f, test_case_id: detail.value }))}
                disabled={!!editingCase}
                placeholder="unique-test-id"
              />
            </FormField>
            <FormField label={t("datasets.col.suite")}>
              <SpaceBetween size="xs">
                <Select
                  selectedOption={form.suite === "__new__" ? { label: t("datasets.modal.newSuite"), value: "__new__" } : form.suite ? { label: form.suite, value: form.suite } : null}
                  onChange={({ detail }) => setForm((f) => ({ ...f, suite: detail.selectedOption.value || "" }))}
                  options={[...suites.map((s) => ({ label: s, value: s })), { label: t("datasets.modal.newSuite"), value: "__new__" }]}
                  placeholder={t("datasets.modal.selectSuite")}
                  filteringType="auto"
                  disabled={!!editingCase}
                />
                {form.suite === "__new__" && (
                  <Input
                    value={form.suite === "__new__" ? (form as any)._newSuite || "" : ""}
                    onChange={({ detail }) => setForm((f) => ({ ...f, _newSuite: detail.value } as any))}
                    placeholder="new-suite-name"
                    autoFocus
                  />
                )}
              </SpaceBetween>
            </FormField>
            <FormField label={t("datasets.modal.metrics")} description={t("datasets.modal.metricsDesc")}>
              {/* form.metrics stays a comma-separated string (submit/seed already
                  speak that shape); convert only at this boundary. Options are
                  the fixed list PLUS any already-selected value not in it, so a
                  legacy custom metric still shows as selected rather than vanishing. */}
              <Multiselect
                selectedOptions={form.metrics
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .map((m) => ({ label: m, value: m }))}
                onChange={({ detail }) =>
                  setForm((f) => ({
                    ...f,
                    metrics: detail.selectedOptions.map((o) => o.value).join(", "),
                  }))
                }
                options={Array.from(
                  new Set([
                    ...METRICS_OPTIONS,
                    ...form.metrics.split(",").map((s) => s.trim()).filter(Boolean),
                  ])
                ).map((m) => ({ label: m, value: m }))}
                placeholder={t("datasets.modal.selectMetrics")}
                filteringType="auto"
              />
            </FormField>
          </Grid>

          {/* Row 2 */}
          <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
            <FormField label={t("datasets.col.status")}>
              <Select
                selectedOption={{ label: statusLabel(form.status), value: form.status }}
                onChange={({ detail }) => setForm((f) => ({ ...f, status: detail.selectedOption.value || "active" }))}
                options={STATUS_OPTIONS.map((s) => ({ label: statusLabel(s), value: s }))}
              />
            </FormField>
            <FormField label={t("datasets.modal.channelType")}>
              <Select
                selectedOption={{ label: form.channel_type, value: form.channel_type }}
                onChange={({ detail }) => setForm((f) => ({ ...f, channel_type: detail.selectedOption.value || "rocky" }))}
                options={CHANNEL_TYPE_OPTIONS.map((c) => ({ label: c, value: c }))}
              />
            </FormField>
            <FormField
              label={t("datasets.col.profile")}
              description={t("datasets.modal.profileDesc")}
            >
              <Select
                selectedOption={
                  evaluationProfiles
                    .filter((profile) => profile.profile_id === form.personal_profile)
                    .map((profile) => ({
                      label: profile.display_alias,
                      value: profile.profile_id,
                      description: profile.profile_id,
                      disabled: !profile.enabled,
                    }))[0] || null
                }
                onChange={({ detail }) =>
                  setForm((f) => ({
                    ...f,
                    personal_profile: detail.selectedOption.value || "",
                  }))
                }
                options={evaluationProfiles.map((profile) => ({
                  label: profile.enabled
                    ? profile.display_alias
                    : t("datasets.modal.profileDisabled", { alias: profile.display_alias }),
                  value: profile.profile_id,
                  description: profile.profile_id,
                  disabled: !profile.enabled,
                }))}
                placeholder={t("datasets.modal.selectProfile")}
                filteringType="auto"
                empty={t("datasets.modal.noProfiles")}
              />
            </FormField>
          </Grid>

          {/* Row 3 */}
          <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
            <FormField label={t("datasets.modal.expectedTools")} description={t("datasets.modal.commaSeparated")}>
              <Input
                value={form.expected_tools}
                onChange={({ detail }) => setForm((f) => ({ ...f, expected_tools: detail.value }))}
                placeholder="tool1, tool2"
              />
            </FormField>
            <FormField
              label={t("datasets.modal.domainTags")}
              description={t("datasets.modal.domainTagsDesc")}
            >
              <Input
                value={form.domains_tag}
                onChange={({ detail }) => setForm((f) => ({ ...f, domains_tag: detail.value }))}
                placeholder="cmhk-sales, cmhk-sales/revenue"
              />
            </FormField>
          </Grid>

          <FormField label={t("datasets.modal.runAgent")} description={t("datasets.modal.runAgentDesc")}>
            <Select
              selectedOption={
                form.run_agent
                  ? { label: form.run_agent, value: form.run_agent }
                  : { label: t("datasets.modal.runAgentDefault"), value: "" }
              }
              onChange={({ detail }) => setForm((f) => ({ ...f, run_agent: detail.selectedOption.value || "" }))}
              options={[
                { label: t("datasets.modal.runAgentDefault"), value: "" },
                ...SKILL_TAG_AGENTS.map((a) => ({ label: a, value: a })),
              ]}
            />
          </FormField>

          {/* Skill tags — scoped to an owning agent. The agent is a bounded
              select (not free text) because the eval backend matches cases on
              (agent, skill) jointly; the Skill Tags field holds bare names that
              all share the selected agent. */}
          <Grid gridDefinition={[{ colspan: 4 }, { colspan: 8 }]}>
            <FormField
              label={t("datasets.modal.skillAgent")}
              description={form.skills_tag.trim() ? t("datasets.modal.skillAgentDesc") : t("datasets.modal.skillAgentNoTags")}
            >
              <Select
                disabled={!form.skills_tag.trim()}
                selectedOption={{ label: form.skill_agent, value: form.skill_agent }}
                onChange={({ detail }) => setForm((f) => ({ ...f, skill_agent: detail.selectedOption.value || SKILL_TAG_AGENTS[0] }))}
                options={SKILL_TAG_AGENTS.map((a) => ({ label: a, value: a }))}
              />
            </FormField>
            <FormField label={t("datasets.modal.skillTags")} description={t("datasets.modal.skillTagsDesc")}>
              <Input
                value={form.skills_tag}
                onChange={({ detail }) => setForm((f) => ({ ...f, skills_tag: detail.value }))}
                placeholder="search, summarize, ..."
              />
            </FormField>
          </Grid>

          {/* Checkboxes */}
          <SpaceBetween direction="horizontal" size="xl">
            <Checkbox
              checked={form.online_ignore}
              onChange={({ detail }) => setForm((f) => ({ ...f, online_ignore: detail.checked }))}
            >
              {t("datasets.modal.onlineIgnore")}
            </Checkbox>
            <Checkbox
              checked={form.is_scheduled}
              onChange={({ detail }) => setForm((f) => ({ ...f, is_scheduled: detail.checked }))}
            >
              {t("datasets.modal.scheduled")}
            </Checkbox>
            <Checkbox
              checked={form.integration_test}
              onChange={({ detail }) => setForm((f) => ({ ...f, integration_test: detail.checked }))}
            >
              {t("datasets.modal.integration")}
            </Checkbox>
          </SpaceBetween>

          {/* Input Mode Toggle — locked when editing an existing case */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>{t("datasets.modal.inputMode")}</span>
              {(() => {
                const disableSingle = !!editingCase && !!(editingCase.turns && editingCase.turns.length > 1);
                const disableMulti = !!editingCase && !(editingCase.turns && editingCase.turns.length > 1);
                return (
                  <div style={{ display: "flex", gap: 0, border: "1px solid #e9ebed", borderRadius: 20, overflow: "hidden" }}>
                    <button
                      disabled={disableSingle}
                      onClick={() => !disableSingle && setForm((f) => ({ ...f, input_mode: "single" as const }))}
                      style={{ padding: "4px 14px", fontSize: 13, border: "none", cursor: disableSingle ? "not-allowed" : "pointer", background: form.input_mode === "single" ? "#0073bb" : "transparent", color: form.input_mode === "single" ? "#fff" : disableSingle ? "#b4b4bb" : "#414d5c", fontWeight: 600, borderRadius: 20, opacity: disableSingle ? 0.5 : 1 }}
                    >{t("datasets.singleTurn")}</button>
                    <button
                      disabled={disableMulti}
                      onClick={() => !disableMulti && setForm((f) => ({ ...f, input_mode: "multi" as const }))}
                      style={{ padding: "4px 14px", fontSize: 13, border: "none", cursor: disableMulti ? "not-allowed" : "pointer", background: form.input_mode === "multi" ? "#0073bb" : "transparent", color: form.input_mode === "multi" ? "#fff" : disableMulti ? "#b4b4bb" : "#414d5c", fontWeight: 600, borderRadius: 20, opacity: disableMulti ? 0.5 : 1 }}
                    >{t("datasets.multiTurn")}</button>
                  </div>
                );
              })()}
            </div>

            {form.input_mode === "single" ? (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t("datasets.modal.inputPrompt")}</div>
                <Textarea
                  value={form.input_prompt}
                  onChange={({ detail }) => setForm((f) => ({ ...f, input_prompt: detail.value }))}
                  placeholder={t("datasets.modal.inputPromptPlaceholder")}
                  rows={4}
                />
              </div>
            ) : (
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{t("datasets.modal.turns")}</div>
                {form.turns.map((turn, idx) => (
                  <div key={idx} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t("datasets.modal.turn", { n: idx + 1 })}</div>
                      <Textarea
                        value={turn}
                        onChange={({ detail }) => updateTurn(idx, detail.value)}
                        placeholder={t("datasets.modal.turnPlaceholder", { n: idx + 1 })}
                        rows={4}
                      />
                    </div>
                    {idx === form.turns.length - 1 ? (
                      <button
                        onClick={addTurn}
                        style={{ width: 32, height: 32, borderRadius: "50%", border: "2px solid #0073bb", background: "none", color: "#0073bb", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                      >+</button>
                    ) : form.turns.length > 1 ? (
                      <Button iconName="close" variant="icon" onClick={() => removeTurn(idx)} />
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Expected Output */}
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
              <SafeTrans t={t} i18nKey="datasets.modal.expectedOutput" components={{ em: <em style={{ fontWeight: 400 }} /> }} />
            </div>
            <Textarea
              value={form.expected_output}
              onChange={({ detail }) => setForm((f) => ({ ...f, expected_output: detail.value }))}
              placeholder={t("datasets.modal.placeholder")}
              rows={4}
            />
          </div>

          {/* Context */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>{t("datasets.modal.context")}</span>
              <div style={{ display: "flex", gap: 0, border: "1px solid #e9ebed", borderRadius: 20, overflow: "hidden" }}>
                <button onClick={() => setForm((f) => ({ ...f, context_mode: "raw" as const }))} style={{ padding: "4px 14px", fontSize: 13, border: "none", cursor: "pointer", background: form.context_mode === "raw" ? "#0073bb" : "transparent", color: form.context_mode === "raw" ? "#fff" : "#414d5c", fontWeight: 600, borderRadius: 20 }}>{t("datasets.modal.rawJson")}</button>
                <button onClick={() => setForm((f) => ({ ...f, context_mode: "structured" as const }))} style={{ padding: "4px 14px", fontSize: 13, border: "none", cursor: "pointer", background: form.context_mode === "structured" ? "#0073bb" : "transparent", color: form.context_mode === "structured" ? "#fff" : "#414d5c", fontWeight: 600, borderRadius: 20 }}>{t("datasets.modal.structured")}</button>
              </div>
            </div>
            {form.context_mode === "raw" ? (
              <Textarea
                value={form.context_raw}
                onChange={({ detail }) => setForm((f) => ({ ...f, context_raw: detail.value }))}
                placeholder={t("datasets.modal.placeholder")}
                rows={5}
              />
            ) : (
              <ContextStructuredEditor contextRaw={form.context_raw} onChange={(v) => setForm((f) => ({ ...f, context_raw: v }))} />
            )}
          </div>
        </SpaceBetween>
      </Modal>


      {/* Import Result Modal */}
      <Modal
        visible={importResult !== null}
        onDismiss={() => setImportResult(null)}
        header={t("datasets.importResult.title")}
        footer={<Box float="right"><Button variant="primary" onClick={() => setImportResult(null)}>{t("datasets.importResult.close")}</Button></Box>}
      >
        {importResult && (
          <SpaceBetween size="m">
            <div style={{ display: "flex", gap: 24 }}>
              <div><strong style={{ fontSize: 24, color: "#00802f" }}>{importResult.imported}</strong><div style={{ fontSize: 12, color: "#8c8c94" }}>{t("datasets.importResult.imported")}</div></div>
              <div><strong style={{ fontSize: 24, color: "#db0000" }}>{importResult.errors}</strong><div style={{ fontSize: 12, color: "#8c8c94" }}>{t("datasets.importResult.errors")}</div></div>
            </div>
            {importResult.details.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{t("datasets.importResult.details")}</div>
                <div style={{ maxHeight: 300, overflow: "auto", border: "1px solid #e9ebed", borderRadius: 8, padding: 12 }}>
                  {importResult.details.map((d, i) => (
                    <div key={i} style={{ padding: "6px 0", borderBottom: i < importResult.details.length - 1 ? "1px solid #e9ebed" : "none", fontSize: 13 }}>
                      <span style={{ fontWeight: 600, color: "#0f141a" }}>{d.test_case_id}</span>
                      <span style={{ color: "#db0000", marginLeft: 8 }}>{d.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SpaceBetween>
        )}
      </Modal>
      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, padding: "12px 20px", borderRadius: 8, fontSize: 14, fontWeight: 500, color: "#fff", background: toast.type === "success" ? "#00802f" : toast.type === "info" ? "#0073bb" : "#db0000", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 9999, transition: "opacity 0.3s" }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
